import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Form, Button, Table, Spinner } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "../../configs/app";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const fetchBlockedDates = async ({ queryKey }) => {
  const response = await axios.get(`${API_URL}/blocked-dates?hotelSlug=${queryKey[1]}`);
  return response.data;
};

const BlockedDates = () => {
  const { adminDetails } = useSelector((state) => state.admin);
  const isSuperAdmin = !adminDetails?.hotelId;
  const [hotelSlug, setHotelSlug] = useState(adminDetails?.hotelId?.slug || "the-sojourner-by-genesis");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("Fully booked");
  const [blockedDates, setBlockedDates] = useState([]);

  const { isLoading, refetch } = useQuery(["blockedDates", hotelSlug], fetchBlockedDates, {
    enabled: Boolean(hotelSlug),
    onSuccess: (data) => {
      if (data?.status) {
        setBlockedDates(data.value || []);
      }
    },
    onError: () => toast.error("Unable to load blocked dates"),
  });

  useEffect(() => {
    if (adminDetails?.hotelId?.slug) {
      setHotelSlug(adminDetails.hotelId.slug);
    }
  }, [adminDetails]);

  const hotelOptions = useMemo(() => [
    { value: "the-sojourner-by-genesis", label: "The Sojourner by Genesis" },
    { value: "genesis-reventon-park", label: "Genesis Reventon Park" },
    { value: "genesis-wellness-park", label: "Genesis Wellness Park" },
    { value: "genesis-castle", label: "Genesis Castle" },
    { value: "genesis-jersey-estate", label: "Genesis Jersey Estate" },
  ], []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate || !hotelSlug) return toast.warning("Select a hotel and a date range first");
    if (new Date(endDate) < new Date(startDate)) return toast.warning("End date must be on or after the start date");

    try {
      const response = await axios.post(`${API_URL}/blocked-dates/create`, { hotelSlug, startDate, endDate, note });
      if (response.data.status) {
        toast.success("Blocked dates saved");
        setStartDate("");
        setEndDate("");
        setNote("Fully booked");
        refetch();
      } else {
        toast.error(response.data.errors?.[0]?.msg || "Unable to save blocked dates");
      }
    } catch (error) {
      toast.error("Unable to save blocked date");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.post(`${API_URL}/blocked-dates/delete`, { id });
      if (response.data.status) {
        toast.success("Blocked date removed");
        refetch();
      } else {
        toast.error(response.data.errors?.[0]?.msg || "Unable to delete blocked date");
      }
    } catch (error) {
      toast.error("Unable to delete blocked date");
    }
  };

  return (
    <AdminLayout>
      <div className="container-fluid">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800">Blocked Dates</h1>
        </div>

        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">Mark days as unavailable</h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave} className="row g-3 align-items-end">
              {isSuperAdmin && (
                <div className="col-md-4">
                  <Form.Label>Hotel</Form.Label>
                  <Form.Select value={hotelSlug} onChange={(e) => setHotelSlug(e.target.value)}>
                    {hotelOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </div>
              )}
              <div className="col-md-3">
                <Form.Label>Start date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="col-md-3">
                <Form.Label>End date</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <div className="col-md-3">
                <Form.Label>Note</Form.Label>
                <Form.Control type="text" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <div className="col-12">
                <Button type="submit">Save blocked dates</Button>
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">Current blocked dates</h6>
          </div>
          <div className="card-body">
            {isLoading ? (
              <Spinner animation="border" />
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Hotel</th>
                    <th>Date</th>
                    <th>Note</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {blockedDates.map((item) => (
                    <tr key={item._id}>
                      <td>{item.hotelSlug}</td>
                      <td>{new Date(item.date).toLocaleDateString()}</td>
                      <td>{item.note}</td>
                      <td>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(item._id)}>Remove</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BlockedDates;
